import React, { useEffect, useState } from "react";
import { Col, Form, Input, Modal, Popconfirm, Row, Select, Table, Tag, Tooltip } from "antd";
import { DeleteOutlined, KeyOutlined } from "@ant-design/icons";
import SvgIcon from "../../const/SvgIcon";
import { useAccess } from "../../hooks/usePermission";
import { hasAccess } from "../../utils/Permission";
import {
  addOrganizationFacility,
  deleteOrganizationFacility,
  fetchFacilityToken,
  fetchOrganizationFacilities,
  regenerateFacilityToken,
  updateOrganizationFacility,
} from "../../redux/Services/OrganizationServices";
import LoadingButton from "../../component/LoadingButton";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import ApiTokenSection from "./ApiTokenSection";

// Same rule as the server: Add/Edit/Delete on "Users Management" or "HIP Dashboard"
export const FACILITY_MODULES = ["Users Management", "HIP Dashboard"];

/**
 * Facilities (ID, name, status) of one organization, with add / edit / delete.
 * `organizationId` is the organization's row id (Organization.Id).
 * Facilities added by anyone (e.g. super admin) for this organization show here.
 */
const FacilitiesSection = ({ organizationId }) => {
  const access = useAccess();
  const canAdd = hasAccess(access, FACILITY_MODULES, "Add");
  const canEdit = hasAccess(access, FACILITY_MODULES, "Edit");
  const canDelete = hasAccess(access, FACILITY_MODULES, "Delete");
  // Facility being deleted (shows a spinner on that row)
  const [deletingId, setDeletingId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  // null: closed, {}: add, facility: edit
  const [editing, setEditing] = useState(null);
  // Facility whose status is being changed (shows a spinner on that row)
  const [togglingId, setTogglingId] = useState(null);
  // Facility whose API token is shown (null: closed)
  const [tokenFacility, setTokenFacility] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    if (!organizationId) return setFacilities([]);
    setLoading(true);
    setFacilities(await fetchOrganizationFacilities(organizationId));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [organizationId]);

  const openForm = (facility = {}) => {
    setEditing(facility);
    form.setFieldsValue({
      FacilityId: facility.FacilityId || "",
      FacilityName: facility.FacilityName || "",
      Status: facility.Status === 0 ? 0 : 1,
    });
  };

  const [handleSave, saving] = useAsyncAction(async (values) => {
    const res = editing?.Id
      ? await updateOrganizationFacility(organizationId, editing.Id, values)
      : await addOrganizationFacility(organizationId, values);
    if (res) {
      setEditing(null);
      load();
    }
  });

  // Locked while a change is running, so a double click doesn't flip it back
  const [toggleStatus] = useAsyncAction(async (item) => {
    if (!canEdit) return;
    setTogglingId(item.Id);
    try {
      const res = await updateOrganizationFacility(organizationId, item.Id, {
        Status: item.Status ? 0 : 1,
      });
      if (res) await load();
    } finally {
      setTogglingId(null);
    }
  });

  const [handleDelete] = useAsyncAction(async (item) => {
    if (!canDelete) return;
    setDeletingId(item.Id);
    try {
      const res = await deleteOrganizationFacility(organizationId, item.Id);
      if (res) await load();
    } finally {
      setDeletingId(null);
    }
  });

  const columns = [
    {
      title: "S.No.",
      key: "sno",
      className: "table_first_col",
      render: (value, item, index) => index + 1,
    },
    {
      title: "Facility ID",
      dataIndex: "FacilityId",
      key: "FacilityId",
      render: (value, item) => (
        <>
          {value} {item.IsDefault ? <Tag color="blue">Default</Tag> : null}
        </>
      ),
    },
    { title: "Facility Name", dataIndex: "FacilityName", key: "FacilityName" },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      className: "table_status_col",
      render: (value, item) => (
        <Tooltip title={canEdit ? "Click to change" : ""}>
          <span
            className={item?.Status ? "status_active" : "status_inactive"}
            style={{ cursor: togglingId ? "wait" : canEdit ? "pointer" : "default" }}
            onClick={() => toggleStatus(item)}
          >
            {togglingId === item.Id && <span className="btn-spinner mr-5" />}
            {item?.Status ? "Active" : "Inactive"}
          </span>
        </Tooltip>
      ),
    },
    ...(canEdit || canDelete
      ? [
          {
            title: "Actions",
            key: "action",
            className: "table_action_col",
            render: (value, item) => (
              <span className="flex align-items-center" style={{ gap: 12 }}>
                {canEdit && (
                  <Tooltip title="API token">
                    <span
                      className="cursor-pointer"
                      style={{ fontSize: 18, color: "var(--theme-primary)" }}
                      onClick={() => setTokenFacility(item)}
                    >
                      <KeyOutlined />
                    </span>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip title="Edit">
                    <span className="cursor-pointer" onClick={() => openForm(item)}>
                      <SvgIcon.Edit className="scale-icon edit-icon" />
                    </span>
                  </Tooltip>
                )}
                {canDelete &&
                  (deletingId === item.Id ? (
                    <span className="btn-spinner" />
                  ) : (
                    <Popconfirm
                      title={
                        <>
                          Delete facility <b>{item.FacilityName}</b>?
                          <br />
                          This cannot be undone.
                          {item.IsDefault ? (
                            <>
                              <br />
                              Another facility will become the default.
                            </>
                          ) : null}
                        </>
                      }
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={() => handleDelete(item)}
                    >
                      <Tooltip title="Delete">
                        <span className="cursor-pointer" style={{ color: "#e5484d", fontSize: 18 }}>
                          <DeleteOutlined />
                        </span>
                      </Tooltip>
                    </Popconfirm>
                  ))}
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex align-items-center">
        <h3 className="flex1">Facilities ({facilities.length})</h3>
        {canAdd && organizationId && (
          <Tooltip title="Add Facility">
            <span className="jop_btn" onClick={() => openForm()}>
              Add
              <SvgIcon.Add width={40} height={40} className="cursor-pointer" />
            </span>
          </Tooltip>
        )}
      </div>
      <div className="tableResponsive mt-10">
        <Table
          rowKey={(row) => row?.Id ?? row?.id}
          loading={loading}
          className="table antd_table"
          bordered
          columns={columns}
          dataSource={facilities}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </div>

      <Modal
        title={tokenFacility ? `${tokenFacility.FacilityName} (${tokenFacility.FacilityId})` : ""}
        open={!!tokenFacility}
        onCancel={() => setTokenFacility(null)}
        footer={null}
        width={640}
        destroyOnClose
      >
        {tokenFacility && (
          <ApiTokenSection
            tokenKey={tokenFacility.Id}
            fetchToken={() => fetchFacilityToken(organizationId, tokenFacility.Id)}
            regenerateToken={() => regenerateFacilityToken(organizationId, tokenFacility.Id)}
            title="Facility API Token"
            entityLabel="facility"
            description={
              <>
                The facility sends this in the <code>token</code> header to push reports
                (<code>POST /api/v3/external/patient-reports</code>). The facility and
                organization are taken from this token only.
              </>
            }
          />
        )}
      </Modal>

      <Modal
        title={editing?.Id ? "Edit Facility" : "Add Facility"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" autoComplete="off" onFinish={handleSave}>
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item
                label="Facility ID"
                name="FacilityId"
                rules={[
                  { required: true, whitespace: true, message: "Facility ID is required" },
                  { max: 100, message: "Max 100 characters allowed" },
                ]}
              >
                <Input placeholder="e.g. IN0610090427" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Facility Name"
                name="FacilityName"
                rules={[
                  { required: true, whitespace: true, message: "Facility Name is required" },
                  { max: 255, message: "Max 255 characters allowed" },
                ]}
              >
                <Input placeholder="Facility Name" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Status" name="Status">
                <Select>
                  <Select.Option value={1}>Active</Select.Option>
                  <Select.Option value={0}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div className="text-center">
            <LoadingButton type="submit" className="gradient-button w-auto" loading={saving}>
              {saving ? "Saving..." : "Submit"}
            </LoadingButton>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default FacilitiesSection;
